import React, { FormEvent } from 'react'
import { useIntl } from 'react-intl'
import { connect, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import { subYears } from 'date-fns'
import { AnyAction, bindActionCreators, Dispatch } from 'redux'
import { Field, formValueSelector, reduxForm } from 'redux-form/immutable'

import { LICENSE_PROGRAM_TYPE_DISPLAY_NAMES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/treatmentOptionsMessages'
import { ClientLicenseSessionInfo } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { ClientSessionData } from '@lyrahealth-inc/shared-app-logic/src/models/client/ClientSessionData'
import PrimaryButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/primaryButton/PrimaryButton'
import BootstrapContainer from '@lyrahealth-inc/ui-core/lib/molecules/layouts/bootstrapContainer/BootstrapContainer'
import CardMedium from '@lyrahealth-inc/ui-core/lib/molecules/layouts/cardMedium/CardMedium'
import DataTable from '@lyrahealth-inc/ui-core/lib/molecules/table/DataTable'
import { InputFieldRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { SelectFieldRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/SelectField'

import styles from './sessionLookup.module.scss'
import { getClientSession } from '../../../clients/data/clientsDataActions'
import { getClientsDataSessionData } from '../../../clients/data/clientsDataSelectors'
import { RootState } from '../../../data/store'
import { programString, ProgramStringKey } from '../../constants/appConstants'
import { PROVIDERS_DETAILS_PAYMENT_HISTORY } from '../../constants/routingConstants'
import validation from '../../utils/validation'
import { ClientSessionLimitInfo } from '../client/ClientSessionLimitInfo'

const tableColumns = [
  {
    Header: 'Provider Name',
    accessor: 'providerName',
  },
  {
    Header: 'Program',
    accessor: 'program',
  },
  {
    Header: 'Session Type',
    accessor: 'sessionType',
  },
  {
    Header: 'Visit Date',
    accessor: 'visitDate',
  },
  {
    Header: 'Attendance',
    accessor: 'attendance',
  },
  {
    Header: 'Specialty Care',
    accessor: 'specialtyCare',
  },
  {
    Header: 'Company',
    accessor: 'displayedCompany',
  },
]

export const SessionLookup: React.FC<SessionLookupProps> = ({
  lyraIds,
  searchYear,
  submitting,
  actions: { getClientSession },
}) => {
  const clientData: ClientSessionData[] = useSelector(getClientsDataSessionData) ?? []
  const { formatMessage, formatDate } = useIntl()
  const navigate = useNavigate()
  const _rowClickHandler = (_: any, rowInfo: { original: { providerId: string } }): void => {
    navigate(PROVIDERS_DETAILS_PAYMENT_HISTORY.route, { state: { provider_id: rowInfo.original.providerId } })
  }

  const clientSessionDisplay = (clientData: ClientSessionData[]) => {
    return clientData.map((ele: ClientSessionData) => {
      return (
        <DataTable
          key={ele.id}
          pageSize={ele.charges_for_session_lookup.length}
          className={styles.card}
          destinationSelector='providerId'
          // @ts-expect-error TS(2322): Type '(_: any, rowInfo: {    original: {        pr... Remove this comment to see the full error message
          rowClickHandler={_rowClickHandler}
          data={ele.charges_for_session_lookup}
          columns={tableColumns}
        />
      )
    })
  }

  const displaySessionUsageByLicense = (licenses?: ClientLicenseSessionInfo[]) => {
    if (!licenses || !licenses.length) {
      return null
    }
    const filteredLicenses = licenses.filter((license) => license.program_type in LICENSE_PROGRAM_TYPE_DISPLAY_NAMES)
    if (!filteredLicenses.length) {
      return null
    }
    const licenseUsage = filteredLicenses.map((license) => {
      const programType = license.program_type as keyof typeof LICENSE_PROGRAM_TYPE_DISPLAY_NAMES
      const programTypeLabel = LICENSE_PROGRAM_TYPE_DISPLAY_NAMES[programType]
      const programLabel = formatMessage(programTypeLabel)
      return (
        <div key={license.license_id}>
          <p>
            <b>{programLabel} sessions: </b> {license.sessions_used ?? 0} of {license.sessions_total} used
          </p>
          <p>
            <b>{programLabel} access start date: </b>{' '}
            {formatDate(license.access_start_date, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
            })}
          </p>
          <p>
            <b>{programLabel} access end date: </b>{' '}
            {formatDate(license.access_end_date, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
            })}
          </p>
        </div>
      )
    })

    return (
      <span className={styles.resultLine}>
        <div>
          <p>
            <b>License Sessions: </b>
          </p>
          {licenseUsage}
        </div>
      </span>
    )
  }

  const displaySessionUsageByCompany = (program: ProgramStringKey, sessionInfo: any, isMinor: boolean = false) => {
    const companySessionList = Object.keys(sessionInfo).map((customerName, index) => {
      if (customerName === 'crossover') {
        return null
      }
      const sessionLimit = sessionInfo[customerName].session_limit
      const sessionUsed = sessionInfo[customerName].benefit_year_usage
      const displayVisitsPerIssuePerYear = sessionInfo[customerName].display_visits_per_issue_per_year
      const displayVisitsPerIssuePerYearByProgram =
        sessionInfo[customerName].display_visits_per_issue_per_year_by_program
      const benefitResetDate = sessionInfo[customerName].benefit_reset_date
      const sessionInfoByProgram = sessionInfo[customerName].session_info_by_program
      const thirdPartySessionCount = sessionInfo[customerName]?.['benefit_year_third_party_session_count']
      let sessionCountNode: string | React.ReactNode = (
        <ClientSessionLimitInfo
          program={program}
          sessionUsed={sessionUsed}
          sessionLimit={sessionLimit}
          displayVisitsPerIssuePerYear={displayVisitsPerIssuePerYear}
          displayVisitsPerIssuePerYearByProgram={displayVisitsPerIssuePerYearByProgram}
          usedSessionsByProgram={sessionInfoByProgram?.benefit_year_usage}
          EAPSessionLimitByProgram={sessionInfoByProgram?.session_limit}
          thirdPartySessionCount={thirdPartySessionCount}
          companyName={customerName}
          benefitResetDate={benefitResetDate}
          isMinor={isMinor}
        />
      )
      if (!sessionCountNode) {
        if (sessionUsed) {
          sessionCountNode = `${sessionUsed} sessions used.`
        } else {
          sessionCountNode = '-'
        }
      }
      return (
        <div key={index}>
          <b> {customerName}</b>: {sessionCountNode}
        </div>
      )
    })

    return (
      <span className={styles.resultLine}>
        <div>
          <p>
            <b>{programString[program]} Sessions: </b>
            {companySessionList}
          </p>
        </div>
      </span>
    )
  }

  const getResultDisplay = (clientData: ClientSessionData[], searchYear: number) => {
    // Will use the selected year or default to the current year if one is not present for any reason
    const selectedYear = searchYear ? searchYear : new Date().getFullYear()
    return clientData.map((client: ClientSessionData, index: number) => {
      return (
        <div key={index}>
          <span className={styles.sessionHeader}>
            <p>
              <b>Client Info for Current Benefit Year</b>
            </p>
          </span>
          <span className={styles.resultLine}>
            <div>
              <p>
                <b>Client ID: </b>
                {client.id}
              </p>
              <p>
                <b>HealthPlan Supported: </b>
                {Object.prototype.hasOwnProperty.call(client, 'health_plan_status') ? 'True' : 'False'}
              </p>
              <p>
                <b>HealthPlan Eligibility: </b>
                {client.health_plan_status || '-'}
              </p>
            </div>
            <div>
              <p>
                <b>Company: </b>
                {client.displayed_employer}
              </p>
              <p>
                <b>LyraCode Enabled: </b>
                {client.lyra_code_enabled ? 'True' : 'False'}
              </p>
              <p>
                <b>LyraCode: </b>
                {client.friendly_lyra_code || '-'}
              </p>
            </div>
            <span>
              <div>
                <p>
                  <b>HealthPlan Error Status: </b>
                  {client.health_plan_info || '-'}
                </p>
              </div>
            </span>
          </span>
          {displaySessionUsageByCompany('eap', client.eap_session_info, client.is_minor)}
          {displaySessionUsageByCompany('hp', client.health_plan_session_info)}
          {displaySessionUsageByLicense(client.seat_based_licenses)}
          <span className={styles.sessionHeader}>
            <p>
              <b>Sessions for Calendar Year {selectedYear}</b>
            </p>
          </span>
          {clientSessionDisplay(clientData)}
        </div>
      )
    })
  }

  const submitFunction = (e: FormEvent) => {
    e.preventDefault()
    return getClientSession(lyraIds.trim().split('\n'), searchYear)
  }

  //This allows us to search for the last 5 years of sessions.
  const searchYearOptions = Array.from({ length: 5 }, (_, i) => {
    const year = subYears(new Date(), i).getFullYear()
    return { label: String(year), value: year }
  })

  return (
    <BootstrapContainer col='col-md-16'>
      <CardMedium>
        <h2>Client Session Counts and Limits</h2>
        <form className={styles.idForm} onSubmit={(e) => submitFunction(e)}>
          <div className='row'>
            <div className='col-xs-12 col-sm-6'>
              <Field name='lyraIds' component={InputFieldRFF} placeholder='Lyra Id' readOnly={submitting} />
            </div>
            <div className='col-xs-12 col-sm-6'>
              <Field
                name='searchYear'
                component={SelectFieldRFF}
                options={searchYearOptions}
                placeholder='Select Year'
                disabled={submitting}
              />
            </div>
          </div>
          <PrimaryButton isLoading={submitting} id='submit' type='submit'>
            Submit
          </PrimaryButton>
        </form>
        {getResultDisplay(clientData, searchYear)}
      </CardMedium>
    </BootstrapContainer>
  )
}

type SessionLookupProps = {
  lyraIds: string
  searchYear: number
  submitting: boolean
  actions: { getClientSession: (lyraIds: string[], searchYear: number) => Promise<void> }
}

const mapStateToProps = (state: RootState) => {
  const selector = formValueSelector('clientSession')
  return {
    lyraIds: selector(state, 'lyraIds'),
    searchYear: selector(state, 'searchYear'),
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => {
  return {
    actions: bindActionCreators({ getClientSession }, dispatch),
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: 'clientSession',
    validate: ($$values) =>
      validation.requiredFields($$values, [
        { fieldName: 'lyraIds', errorText: 'Lyra Id' },
        { fieldName: 'searchYear', errorText: 'Year' },
      ]),
    touchOnBlur: true,
    // @ts-expect-error TS(2345): Argument of type 'FC<SessionLookupProps>' is not a... Remove this comment to see the full error message
  })(SessionLookup),
)
